import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, liveSocket, type SavedView, type Scope, type WireLine } from '@/api/client';
import { ColumnsHeader } from '@/components/ColumnsHeader';
import { Icon } from '@/components/Icon';
import { Facets } from '@/components/Facets';
import { Histogram } from '@/components/Histogram';
import { Inspector } from '@/components/Inspector';
import { LogList } from '@/components/LogList';
import { NeverSent, useNeverSent } from '@/components/NeverSent';
import { PatternList } from '@/components/PatternList';
import { QueryRow } from '@/components/QueryRow';
import { ViewsMenu } from '@/components/ViewsMenu';
import { newRuleLink } from '@/lib/alerts';
import { columnsFrom, EXTRA_COLUMNS, gridTemplate } from '@/lib/columns';
import { ago, clockIn, dayKey, formatCount, lineTime, RANGES, shortStamp, zoneLabel } from '@/lib/format';
import { useNow, useWidth } from '@/lib/hooks';
import { Link, navigate } from '@/lib/router';
import { updateSettings, useSettings } from '@/lib/settings';
import { type Command, shellKey, usePageCommands, useShell } from '@/lib/shell';
import { isIncomplete, loadHistory, remember } from '@/lib/suggest';
import { termState, withoutField, withoutTerm, withTerm } from '@/lib/terms';
import { queryWords } from '@/lib/text';
import { extendBack, presetOf, rangeFromParams, rangeToParams, resolveRange, stampRange, type TimeRange, zoomIn, zoomOut } from '@/lib/time';

const AROUND_MS = 5 * 60_000;
const MAX_LIVE_LINES = 5000;
// Live lines reach the list in calm steps, and stay lit long enough to be seen arriving.
const LIVE_FLUSH_MS = 400;
const FRESH_MS = 1600;

function readUrl(): { q: string; range: TimeRange; line: string | null; columns: string[] } {
    const params = new URLSearchParams(location.search);
    const known = new Set(EXTRA_COLUMNS.map((column) => column.key));

    return {
        q: params.get('q') ?? '',
        range: rangeFromParams(params),
        line: params.get('line'),
        columns: (params.get('cols') ?? '').split(',').filter((key) => known.has(key)),
    };
}

// The home: the query, the time, the histogram, the facets, the lines (or their patterns), the inspector, live. A new
// environment is a new mount (the shell keys it), so nothing of the last one lingers; the query and the range carry
// over through the URL.
export function Explore({ mode }: { mode: 'lines' | 'patterns' }): ReactNode {
    const [initial] = useState(readUrl);
    const settings = useSettings();
    const { timeZone: zone, wrap, showRequests } = settings;
    const shell = useShell();
    const { env, envs, privacy, say } = shell;

    const [input, setInput] = useState(initial.q);
    const [q, setQ] = useState(initial.q);
    const [recent, setRecent] = useState(loadHistory);
    const [range, setRange] = useState<TimeRange>(initial.range);
    const [rangeOpen, setRangeOpen] = useState(false);
    const [anchor, setAnchor] = useState(() => Date.now());
    const [columnKeys, setColumnKeys] = useState<string[]>(initial.columns);
    const [selected, setSelected] = useState<WireLine | null>(null);
    const [scrollToId, setScrollToId] = useState<string | null>(null);
    const [live, setLive] = useState(false);
    // When live started and, once stopped, when it stopped: the marker above the search says so.
    const [liveSpan, setLiveSpan] = useState<{ since: number; until: number | null } | null>(null);
    const [liveLines, setLiveLines] = useState<WireLine[]>([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
    const [liveRate, setLiveRate] = useState<number | null>(null);
    const [scrolledAway, setScrolledAway] = useState(false);
    const [liveNow, setLiveNow] = useState(() => Date.now());
    const clock = useNow(5000);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    // Lines received and not shown yet, oldest first: what live holds while it is paused.
    const incoming = useRef<WireLine[]>([]);
    const columns = useMemo(() => columnsFrom(columnKeys), [columnKeys]);
    const [logsRef, logsWidth] = useWidth<HTMLDivElement>();
    const compact = logsWidth > 0 && logsWidth < 880;
    // A phone: each line on two rows, wrapped whatever the setting.
    const narrow = logsWidth > 0 && logsWidth < 560;
    const neverSent = useNeverSent(env);

    const resetLive = useCallback(() => {
        incoming.current = [];
        setLiveLines([]);
        setPendingCount(0);
        setLiveSpan((span) => (span && span.until === null ? { since: Date.now(), until: null } : null));
    }, []);
    const apply = useCallback(
        (next: string) => {
            setQ(next);
            setAnchor(Date.now());
            resetLive();
        },
        [resetLive],
    );
    // Typing settles for a moment before the query runs; Enter runs it at once and remembers it. A query that does not
    // parse yet (`service:` waiting for its value) waits for Enter: the facets, and the completions read from them,
    // stay those of the last whole query.
    useEffect(() => {
        if (input === q || isIncomplete(input)) {
            return;
        }
        const timer = setTimeout(() => apply(input), 350);

        return () => clearTimeout(timer);
    }, [input, q, apply]);
    const run = useCallback(
        (next: string) => {
            setInput(next);
            apply(next);
            setRecent((history) => remember(history, next));
        },
        [apply],
    );

    const { from, to } = resolveRange(range, anchor);
    const scope = useMemo<Scope>(() => ({ env, q, from, to }), [env, q, from, to]);
    const multiDay = dayKey(from, zone) !== dayKey(to, zone);

    const search = useInfiniteQuery({
        queryKey: ['search', scope],
        queryFn: ({ pageParam, signal }) => api.search(scope, pageParam, signal),
        initialPageParam: null as string | null,
        getNextPageParam: (last) => last.cursor,
        placeholderData: (previous) => previous,
        enabled: envs.length > 0,
    });
    const histogram = useQuery({
        queryKey: ['histogram', scope],
        queryFn: ({ signal }) => api.histogram(scope, signal),
        placeholderData: (previous) => previous,
        enabled: envs.length > 0,
    });
    const facets = useQuery({
        queryKey: ['facets', scope],
        queryFn: ({ signal }) => api.facets(scope, signal),
        placeholderData: (previous) => previous,
        enabled: envs.length > 0,
    });
    const patterns = useQuery({
        queryKey: ['patterns', scope, showRequests],
        queryFn: ({ signal }) => api.patterns(scope, showRequests, signal),
        placeholderData: (previous) => previous,
        enabled: mode === 'patterns' && envs.length > 0,
    });

    const searchLines = useMemo(() => search.data?.pages.flatMap((page) => page.lines) ?? [], [search.data]);
    // What arrived live stays on top of the search, live or stopped, until the query changes.
    const lines = useMemo(() => {
        if (liveLines.length === 0) {
            return searchLines;
        }
        const seen = new Set(liveLines.map((line) => line.id));

        return [...liveLines, ...searchLines.filter((line) => !seen.has(line.id))];
    }, [liveLines, searchLines]);
    const queryError = search.error instanceof ApiError ? search.error.message : (search.data?.pages[0]?.errors[0]?.message ?? null);
    const words = useMemo(() => queryWords(q), [q]);
    const loading = (search.isFetching && !search.isFetchingNextPage) || histogram.isFetching;

    // The histogram as the search found it, plus every line live brought since: the right edge grows as lines arrive.
    const histogramData = useMemo(() => {
        if (!histogram.data || liveLines.length === 0) {
            return histogram.data;
        }
        const step = histogram.data.stepSeconds * 1000;
        const buckets = new Map(histogram.data.points.map((point) => [point.t, { ...point.counts }]));
        for (const line of liveLines) {
            const t = Math.floor(Date.parse(line.ts) / step) * step;
            const counts = buckets.get(t) ?? {};
            counts[line.level] = (counts[line.level] ?? 0) + 1;
            buckets.set(t, counts);
        }

        return { stepSeconds: histogram.data.stepSeconds, points: [...buckets].map(([t, counts]) => ({ t, counts })) };
    }, [histogram.data, liveLines]);
    const totals = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const point of histogramData?.points ?? []) {
            for (const [level, count] of Object.entries(point.counts)) {
                counts[level] = (counts[level] ?? 0) + count;
            }
        }

        return {
            all: Object.values(counts).reduce((sum, value) => sum + value, 0),
            errors: (counts.error ?? 0) + (counts.fatal ?? 0),
            warnings: counts.warn ?? 0,
        };
    }, [histogramData]);
    const requestDumps = facets.data?.kind?.find((item) => item.value === 'request')?.count ?? 0;

    // A link to one line (?line=…) opens it.
    const linked = useQuery({
        queryKey: ['line', env, initial.line],
        queryFn: () => api.line(env, initial.line as string),
        enabled: Boolean(initial.line) && envs.length > 0,
        staleTime: Infinity,
    });
    // Closing the inspector closes the linked line too: it does not come back from the URL.
    const [linkClosed, setLinkClosed] = useState(false);
    const shown = selected ?? (!linkClosed && initial.line && linked.data ? linked.data.line : null);
    const closeLine = useCallback(() => {
        setSelected(null);
        setLinkClosed(true);
    }, []);

    // The URL is the state: every view is a link.
    useEffect(() => {
        const params = new URLSearchParams({ env });
        if (q) {
            params.set('q', q);
        }
        rangeToParams(params, range);
        if (columnKeys.length > 0) {
            params.set('cols', columnKeys.join(','));
        }
        if (shown) {
            params.set('line', shown.id);
        }
        history.replaceState(null, '', `?${params.toString()}`);
    }, [env, q, range, columnKeys, shown]);

    // Live holds new lines while the reader has scrolled away from the top of the list, and only then: at the top the
    // list follows what arrives, as a tail does.
    const paused = live && scrolledAway;
    const pausedRef = useRef(paused);
    const showRequestsRef = useRef(showRequests);
    useEffect(() => {
        pausedRef.current = paused;
        showRequestsRef.current = showRequests;
    }, [paused, showRequests]);
    const liveShown = useMemo(
        () => (showRequests ? liveLines.length : liveLines.filter((line) => line.kind !== 'request').length),
        [liveLines, showRequests],
    );
    const at = (ms: number): string => clockIn(new Date(ms), zone);

    const showIncoming = useCallback(() => {
        const batch = incoming.current;
        incoming.current = [];
        setPendingCount(0);
        if (batch.length === 0) {
            return;
        }
        setLiveLines((previous) => [...[...batch].reverse(), ...previous].slice(0, MAX_LIVE_LINES));
        const ids = batch.map((line) => line.id);
        setFresh((previous) => new Set([...previous, ...ids]));
        setTimeout(() => {
            setFresh((previous) => {
                const next = new Set(previous);
                ids.forEach((id) => next.delete(id));

                return next;
            });
        }, FRESH_MS);
    }, []);

    // Live tail: the server sends what matches the query as it arrives; the socket comes back by itself.
    useEffect(() => {
        if (!live) {
            return;
        }
        let socket: WebSocket | null = null;
        let stopped = false;
        let retry: ReturnType<typeof setTimeout> | undefined;
        const arrivals: number[] = [];
        const connect = (): void => {
            socket = liveSocket(env, q);
            socket.onmessage = (event: MessageEvent<string>) => {
                const message = JSON.parse(event.data) as { type: string; lines?: WireLine[]; error?: string };
                if (message.type === 'lines' && message.lines) {
                    const at = Date.now();
                    message.lines.forEach(() => arrivals.push(at));
                    incoming.current.push(...message.lines);
                } else if (message.type === 'error') {
                    stopped = true;
                    setLive(false);
                    setLiveRate(null);
                    say(message.error ?? 'Live tail refused the query');
                }
            };
            socket.onclose = () => {
                if (!stopped) {
                    retry = setTimeout(connect, 1500);
                }
            };
        };
        connect();
        const flush = setInterval(() => {
            const cutoff = Date.now() - 10_000;
            while (arrivals.length > 0 && (arrivals[0] as number) < cutoff) {
                arrivals.shift();
            }
            setLiveRate(arrivals.length / 10);
            setLiveNow(Date.now());
            if (pausedRef.current) {
                setPendingCount(
                    showRequestsRef.current ? incoming.current.length : incoming.current.filter((line) => line.kind !== 'request').length,
                );
            } else {
                showIncoming();
            }
        }, LIVE_FLUSH_MS);

        return () => {
            stopped = true;
            clearTimeout(retry);
            clearInterval(flush);
            socket?.close();
        };
    }, [live, env, q, say, showIncoming]);

    const resume = useCallback(() => {
        showIncoming();
        setScrolledAway(false);
        // Once the held lines are in the list and measured (the virtualizer keeps the offset steady as rows above it
        // grow), the list goes back to its top.
        requestAnimationFrame(() => requestAnimationFrame(() => listRef.current?.scrollTo({ top: 0 })));
    }, [showIncoming]);
    const stopLive = useCallback(() => {
        // Stopping keeps what arrived: the lines stay, the marker says when live ran.
        showIncoming();
        setLive(false);
        setLiveRate(null);
        setLiveSpan((span) => (span ? { ...span, until: Date.now() } : null));
    }, [showIncoming]);
    const toggleLive = useCallback(() => {
        if (live) {
            stopLive();

            return;
        }
        // Live is a tail of lines: from the patterns it goes back to them.
        if (mode === 'patterns') {
            navigate(`/${location.search}`);
        }
        incoming.current = [];
        setLiveLines([]);
        setPendingCount(0);
        setSelected(null);
        // Live follows the clock: a fixed range gives way to the preset closest to its span.
        if (range.kind === 'custom') {
            const span = range.to - range.from;
            const preset = RANGES.find((item) => item.ms >= span) ?? (RANGES.at(-1) as (typeof RANGES)[number]);
            setRange({ kind: 'preset', key: preset.key });
        }
        setAnchor(Date.now());
        setLiveNow(Date.now());
        setLiveSpan({ since: Date.now(), until: null });
        setLive(true);
        setScrolledAway(false);
        listRef.current?.scrollTo({ top: 0 });
    }, [live, range, stopLive, mode]);
    // Another environment is another mount: the URL first carries what should follow (the query, the range, the
    // columns), then the shell switches.
    const chooseEnv = useCallback(
        (next: string, carry?: { q: string; range: TimeRange; columns: string[] }) => {
            const params = new URLSearchParams({ env: next });
            const state = carry ?? { q, range, columns: columnKeys };
            if (state.q) {
                params.set('q', state.q);
            }
            rangeToParams(params, state.range);
            if (state.columns.length > 0) {
                params.set('cols', state.columns.join(','));
            }
            history.replaceState(null, '', `${location.pathname}?${params.toString()}`);
            shell.setEnv(next);
        },
        [q, range, columnKeys, shell],
    );
    const chooseRange = useCallback(
        (next: TimeRange) => {
            if (live && next.kind === 'custom') {
                stopLive();
            }
            setRange(next);
            setAnchor(Date.now());
            resetLive();
        },
        [live, stopLive, resetLive],
    );
    const refresh = useCallback(() => {
        if (range.kind === 'preset') {
            setAnchor(Date.now());
        } else {
            void search.refetch();
            void histogram.refetch();
            void facets.refetch();
        }
    }, [range.kind, search, histogram, facets]);
    const setQuery = useCallback((next: string) => run(next), [run]);
    const term = useCallback((field: string, value: string, exclude: boolean) => setQuery(withTerm(q, field, value, exclude)), [q, setQuery]);
    const linesAround = useCallback(
        (line: WireLine) => {
            const at = Date.parse(line.ts);
            chooseRange({ kind: 'custom', from: at - AROUND_MS, to: at + AROUND_MS, label: `± 5 min around ${lineTime(line.ts, zone).clock}` });
            setQuery(`instance:${line.instance}`);
            setScrollToId(line.id);
        },
        [chooseRange, setQuery, zone],
    );
    const select = useCallback((line: WireLine) => {
        setSelected(line);
        setScrollToId(line.id);
    }, []);
    const toggleColumn = useCallback((key: string) => {
        setColumnKeys((keys) => (keys.includes(key) ? keys.filter((item) => item !== key) : [...keys, key]));
    }, []);
    const alertOnQuery = useCallback(() => {
        navigate(newRuleLink({ env, q, name: q ? `${q.slice(0, 60)}${q.length > 60 ? '…' : ''}` : `Every line of ${env}` }));
    }, [env, q]);

    const share = useCallback(() => {
        const params = new URLSearchParams({ env });
        if (q) {
            params.set('q', q);
        }
        rangeToParams(params, { kind: 'custom', from, to });
        if (columnKeys.length > 0) {
            params.set('cols', columnKeys.join(','));
        }
        if (shown) {
            params.set('line', shown.id);
        }
        void navigator.clipboard.writeText(`${location.origin}/?${params.toString()}`);
        say(`Link copied · ${shortStamp(from, zone)} → ${shortStamp(to, zone)} ${zoneLabel(zone)}, fixed`);
    }, [env, q, from, to, columnKeys, shown, zone, say]);
    const copyLineLink = useCallback(() => {
        if (shown) {
            void navigator.clipboard.writeText(`${location.origin}/?env=${shown.env}&line=${shown.id}`);
            say('Link to this line copied');
        }
    }, [shown, say]);
    const visibleLines = useMemo(() => (showRequests ? lines : lines.filter((line) => line.kind !== 'request')), [lines, showRequests]);
    const step = useCallback(
        (direction: 1 | -1) => {
            const index = shown ? visibleLines.findIndex((line) => line.id === shown.id) : -1;
            const next = visibleLines[Math.max(0, Math.min(visibleLines.length - 1, index + direction))];
            if (next) {
                select(next);
            }
        },
        [shown, visibleLines, select],
    );

    // The keyboard map (? shows it). Keys never fire while typing.
    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            const target = event.target as HTMLElement;
            if (
                shellKey(event) ||
                document.querySelector('.scrim') ||
                ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName) ||
                event.metaKey ||
                event.ctrlKey ||
                event.altKey
            ) {
                return;
            }
            const actions: Record<string, () => void> = {
                '/': () => inputRef.current?.focus(),
                j: () => step(1),
                k: () => step(-1),
                Escape: () => {
                    closeLine();
                    setRangeOpen(false);
                },
                l: toggleLive,
                t: () => setRangeOpen((open) => !open),
                r: refresh,
                '[': () => chooseRange(zoomOut(range, Date.now())),
                ']': () => chooseRange(zoomIn(range, anchor)),
                w: () => updateSettings({ wrap: !wrap }),
                u: () => updateSettings({ timeZone: zone === 'rome' ? 'utc' : 'rome' }),
                c: copyLineLink,
            };
            // `/` with Shift is `?` on some keyboards: the shell's.
            const action = event.shiftKey && event.key === '/' ? undefined : actions[event.key];
            if (action) {
                event.preventDefault();
                action();
            }
        };
        window.addEventListener('keydown', onKey);

        return () => window.removeEventListener('keydown', onKey);
    }, [step, closeLine, toggleLive, refresh, chooseRange, range, anchor, wrap, zone, copyLineLink]);

    // A saved view: its query, range and columns here, or in its own environment.
    const applyView = useCallback(
        (view: SavedView) => {
            const viewRange: TimeRange = RANGES.some((preset) => preset.key === view.range) ? { kind: 'preset', key: view.range } : range;
            if (view.env && view.env !== env) {
                chooseEnv(view.env, { q: view.q, range: viewRange, columns: view.columns });

                return;
            }
            run(view.q);
            chooseRange(viewRange);
            setColumnKeys(view.columns);
            say(`View “${view.name}”`);
        },
        [env, range, chooseEnv, run, chooseRange, say],
    );
    const savedViews = useQuery({ queryKey: ['views'], queryFn: api.views, staleTime: 30_000 });
    // What saving a view stores: the range as a preset, the nearest one when the range is fixed.
    const rangeKey =
        range.kind === 'preset' ? range.key : (RANGES.find((preset) => preset.ms >= to - from) ?? (RANGES.at(-1) as (typeof RANGES)[number])).key;

    usePageCommands([
        { icon: '●', label: live ? 'Stop live tail' : 'Start live tail', key: 'L', run: toggleLive },
        { icon: '!', label: q ? 'Alert on this query' : 'Alert on these lines', run: alertOnQuery },
        { icon: '↵', label: wrap ? 'Stop wrapping messages' : 'Wrap long messages', key: 'W', run: () => updateSettings({ wrap: !wrap }) },
        {
            icon: '◷',
            label: zone === 'rome' ? 'Show times in UTC' : 'Show times in Rome',
            key: 'U',
            run: () => updateSettings({ timeZone: zone === 'rome' ? 'utc' : 'rome' }),
        },
        {
            icon: '≡',
            label: showRequests ? 'Hide the request dumps' : 'Show the request dumps',
            run: () => updateSettings({ showRequests: !showRequests }),
        },
        { icon: '⧉', label: 'Copy a link to this view', run: share },
        ...(savedViews.data?.views ?? [])
            .filter((view) => view.env === null || view.env === env || envs.includes(view.env))
            .map((view): Command => ({ icon: '★', label: `View · ${view.name}${view.env ? ` (${view.env})` : ''}`, run: () => applyView(view) })),
        { icon: '☆', label: 'Example · errors and warnings, no request dumps', run: () => setQuery('level:>=warn -kind:request') },
        { icon: '☆', label: 'Example · server errors of the API', run: () => setQuery('service:api status:>=500') },
        { icon: '☆', label: 'Example · the cron, whole', run: () => setQuery('service:cron') },
        ...RANGES.filter((item) => range.kind === 'custom' || item.key !== range.key).map((item) => ({
            icon: '◷',
            label: item.label.toLowerCase(),
            run: () => chooseRange({ kind: 'preset', key: item.key }),
        })),
    ]);

    const further = extendBack(range, anchor);
    // The range in words: "in the last hour", "from 14 Oct 23:02 → 23:12".
    const within =
        range.kind === 'preset'
            ? `in the ${presetOf(range.key).label.toLowerCase()}`
            : range.label
              ? `in ${range.label}`
              : `from ${stampRange(from, to, zone)} ${zoneLabel(zone)}`;
    const empty =
        neverSent && !search.isLoading ? (
            <NeverSent env={env} names={neverSent} />
        ) : envs.length === 0 ? (
            <div className="empty">
                <b>No sources yet</b>
                Create one with <span className="kbd">yarn source:create</span> and point bustard at it.
            </div>
        ) : search.isLoading ? (
            <div className="empty">Loading…</div>
        ) : (
            <div className="empty">
                <b>Nothing here</b>
                {q ? `No line matches the query ${within}.` : `No line ${within}.`}
                <div className="empty-actions">
                    {further && (
                        <button className="act primary" onClick={() => chooseRange(further.range)}>
                            {further.label}
                        </button>
                    )}
                    {q && (
                        <button className="act" onClick={() => run('')}>
                            Clear the query
                        </button>
                    )}
                </div>
            </div>
        );

    const liveWindow = live && range.kind === 'preset';
    const listStyle = { ['--cols' as string]: gridTemplate(columns, compact) } as CSSProperties;
    const levelOn = (level: string): string => (termState(q, 'level', level) === 'inc' ? 'on' : '');

    return (
        <div className="page explore-page">
            <QueryRow
                views={<ViewsMenu env={env} current={{ q, range: rangeKey, columns: columnKeys }} onApply={applyView} say={say} />}
                envs={envs.length > 0 ? envs : [env]}
                input={input}
                onInput={setInput}
                onRun={run}
                error={queryError}
                facets={facets.data}
                scope={{ env, from, to }}
                recent={recent}
                range={range}
                anchor={anchor}
                zone={zone}
                rangeOpen={rangeOpen}
                onRangeOpen={setRangeOpen}
                onRange={chooseRange}
                onShare={share}
                onAlert={alertOnQuery}
                inputRef={inputRef}
            />
            <main className="explore">
                <div className="summary">
                    <div className="big num" title={`${formatCount(totals.all)} lines`}>
                        {formatCount(totals.all)}
                    </div>
                    <div className="cap">
                        {q ? 'lines match the query' : 'lines'} {within}
                    </div>
                    <div className="when">
                        {live ? (
                            <span>following as they arrive</span>
                        ) : range.kind === 'preset' ? (
                            <button onClick={refresh} title="Refresh: the range up to now (R)">
                                <Icon name="refresh" size={13} />
                                updated {ago(anchor, clock)} ago
                            </button>
                        ) : (
                            <button onClick={() => chooseRange(zoomOut(range, Date.now()))} title="Zoom out ([)">
                                <Icon name="zoomout" size={13} />
                                zoom out
                            </button>
                        )}
                    </div>
                    <div className="lvs">
                        <button className={`lvbtn ${levelOn('error')}`} onClick={() => term('level', 'error', false)} title="Only the errors">
                            <i style={{ background: 'var(--critical-ink)' }} />
                            <b>{formatCount(totals.errors)}</b> {totals.errors === 1 ? 'error' : 'errors'}
                        </button>
                        <button className={`lvbtn ${levelOn('warn')}`} onClick={() => term('level', 'warn', false)} title="Only the warnings">
                            <i style={{ background: 'var(--warning)' }} />
                            <b>{formatCount(totals.warnings)}</b> {totals.warnings === 1 ? 'warning' : 'warnings'}
                        </button>
                    </div>
                </div>
                <div className="volume">
                    <Histogram
                        data={histogramData}
                        from={liveWindow ? liveNow - presetOf(range.key).ms : from}
                        to={liveWindow ? liveNow : to}
                        zone={zone}
                        live={live}
                        requestsHidden={!showRequests && requestDumps > 0}
                        onZoom={(start, end) => chooseRange({ kind: 'custom', from: start, to: end })}
                    />
                </div>
                <Facets
                    facets={facets.data}
                    q={q}
                    privacy={privacy}
                    loading={facets.isFetching}
                    onTerm={term}
                    onRemove={(field, value) => setQuery(withoutTerm(q, field, value))}
                    onClear={(field) => setQuery(withoutField(q, field))}
                />
                <section className="stream">
                    <div className="stream-head">
                        <span className="seg">
                            <Link to={`/${location.search}`} className={mode === 'lines' ? 'on' : ''} title="Every line, newest first">
                                Lines
                            </Link>
                            <Link
                                to={`/patterns${location.search}`}
                                className={mode === 'patterns' ? 'on' : ''}
                                title="The lines grouped by their template (G then P)"
                            >
                                Patterns
                            </Link>
                        </span>
                        <button
                            className={`livepill ${live ? (paused ? 'paused' : 'on') : ''}`}
                            onClick={paused ? resume : toggleLive}
                            title={
                                live
                                    ? paused
                                        ? 'Back to the top, following live (L stops)'
                                        : 'Stop following (L)'
                                    : 'Follow the lines as they arrive (L)'
                            }
                        >
                            <i />
                            {live && paused ? 'Paused' : 'Live'}
                            {live && !paused && liveRate !== null && <span className="rate">{liveRate.toFixed(1)}/s</span>}
                        </button>
                        <span className={`liveline ${live ? (paused ? 'paused' : 'on') : ''}`}>
                            {live ? (
                                paused ? (
                                    <>
                                        Scrolled away · <b>{formatCount(pendingCount)} new</b> held
                                    </>
                                ) : (
                                    <>
                                        Following · <b>{formatCount(liveShown)} new</b> since {liveSpan ? at(liveSpan.since) : ''}
                                    </>
                                )
                            ) : q ? (
                                'Matching the query, newest first'
                            ) : (
                                'Newest first'
                            )}
                        </span>
                        <span className="spacer" />
                        <button
                            className={`toggle ${showRequests ? 'on' : ''}`}
                            onClick={() => updateSettings({ showRequests: !showRequests })}
                            title={showRequests ? 'Hide the request dumps' : 'Show the request dumps (95% of the API’s lines)'}
                        >
                            <span className="sw" />
                            Requests <span className="count">{formatCount(requestDumps)}</span>
                        </button>
                        {mode === 'lines' && (
                            <button
                                className={`toggle ${wrap ? 'on' : ''}`}
                                onClick={() => updateSettings({ wrap: !wrap })}
                                title="Wrap long messages (W)"
                            >
                                <span className="sw" />
                                Wrap
                            </button>
                        )}
                    </div>
                    <div
                        ref={logsRef}
                        className={`panel logs ${loading ? 'loading' : ''} ${compact ? 'compact' : ''} ${narrow ? 'narrow' : ''}`}
                        style={listStyle}
                    >
                        {paused && mode === 'lines' && (
                            <button className="newchip" onClick={resume} title="Back to the top, following live">
                                <Icon name="up" size={14} />
                                {pendingCount > 0 ? `${formatCount(pendingCount)} new ${pendingCount === 1 ? 'line' : 'lines'}` : 'Back to live'}
                            </button>
                        )}
                        {mode === 'patterns' ? (
                            <PatternList
                                patterns={patterns.data?.patterns ?? []}
                                total={patterns.data?.total ?? 0}
                                q={q}
                                now={clock}
                                loading={patterns.isFetching}
                                showRequests={showRequests}
                                onQuery={(next, toLines) => {
                                    run(next);
                                    if (toLines) {
                                        navigate(`/${location.search}`);
                                    }
                                }}
                                onSample={(id) => shell.openLine({ env, id })}
                                empty={empty}
                            />
                        ) : (
                            <>
                                <ColumnsHeader
                                    columns={columns}
                                    zone={zone}
                                    onZone={() => updateSettings({ timeZone: zone === 'rome' ? 'utc' : 'rome' })}
                                    onToggle={toggleColumn}
                                />
                                <LogList
                                    key={wrap || narrow ? 'wrap' : 'flat'}
                                    lines={lines}
                                    fresh={fresh}
                                    showRequests={showRequests}
                                    columns={columns}
                                    zone={zone}
                                    wrap={wrap || narrow}
                                    multiDay={multiDay}
                                    liveCount={liveLines.length}
                                    liveLabel={
                                        liveSpan
                                            ? liveSpan.until === null
                                                ? `Live since ${at(liveSpan.since)} · new lines above, history below`
                                                : `Live from ${at(liveSpan.since)} to ${at(liveSpan.until)} · history below`
                                            : null
                                    }
                                    onScrolledAway={setScrolledAway}
                                    selectedId={shown?.id ?? null}
                                    onSelect={select}
                                    onTerm={term}
                                    privacy={privacy}
                                    words={words}
                                    hasMore={search.hasNextPage}
                                    loadingMore={search.isFetchingNextPage}
                                    onMore={() => void search.fetchNextPage()}
                                    extendLabel={further?.label ?? null}
                                    onExtend={() => further && chooseRange(further.range)}
                                    scrollRef={listRef}
                                    scrollToId={scrollToId}
                                    empty={empty}
                                />
                            </>
                        )}
                    </div>
                </section>
                {shown && (
                    <Inspector
                        key={shown.id}
                        line={shown}
                        privacy={privacy}
                        zone={zone}
                        onClose={closeLine}
                        onTerm={term}
                        onQuery={setQuery}
                        onAround={linesAround}
                        onSelect={select}
                        onStep={step}
                        toast={say}
                    />
                )}
            </main>
        </div>
    );
}
